import React from "react";
import PropTypes from "prop-types";
import { Card, CardHeader, CardBody, Row, Col } from "shards-react";
import Chart from "../../utils/chart";
import { useAuth } from '../../context/AuthContext';

class SalesReport extends React.Component {
  constructor(props) {
    super(props);
 
    this.state = {
      chartData: this.props.chartData, // Default chart data
      selectedYear: new Date().getFullYear().toString(), // Default to current year
      loading: true, // Add loading state
    };

    this.legendRef = React.createRef();
    this.canvasRef = React.createRef();
    this.chartInstance = null;
    this.handleYearChange = this.handleYearChange.bind(this);
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }

  handleYearChange(event) {
    this.setState({ selectedYear: event.target.value, loading: true }, () => {
      this.fetchData();
    });
  }

  fetchData() {
    const companyId = localStorage.getItem('company_id');
    const token = localStorage.getItem("token");

    // if (!companyId || companyId === '' || !token || token === '') {
    //   logout();  // Ensure logout is implemented correctly
    //   return;
    // }

    const { os } = this.props; // Get os value from props
    const requestBody = {
      company_id: companyId, 
      os: os, 
      year: this.state.selectedYear
    };

    fetch("https://vaptlabreport.com/shieldapi/device_score_analysis.php", {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",  
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody)
    })
      .then(response => response.json())
      .then(data => {
        if (data.status === "success" && data.data) {
          this.updateChartData(data.data);
        } else {
          this.setState({ loading: false }); 
        }
      })
      .catch(error => {
        console.error("Error fetching data:", error);
        this.setState({ loading: false });
      });
  }

  updateChartData(apiData) {
    const monthLabels = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    const safeData = Array(12).fill(0);
    const partiallySafeData = Array(12).fill(0);
    const unsafeData = Array(12).fill(0);

    monthLabels.forEach((month, index) => {
      if (apiData[month]) {
        safeData[index] = apiData[month].safe_count || 0;
        partiallySafeData[index] = apiData[month].partially_safe_count || 0;
        unsafeData[index] = apiData[month].unsafe_count || 0;
      }
    });

    const newChartData = {
      labels: monthLabels,
      datasets: [
        {
          label: "Safe Device",
          fill: "start",
          data: safeData,
          backgroundColor: "rgb(212, 237, 218)",
          borderColor: "rgb(40, 167, 69)",
          borderWidth: 1.5
        },
        {
          label: "Partially Safe Device",
          fill: "start",
          data: partiallySafeData,
          backgroundColor: "rgb(255, 243, 205)",
          borderColor: "rgb(255, 193, 7)",
          borderWidth: 1.5
        },
        {
          label: "Unsafe Device",
          fill: "start",
          data: unsafeData,
          backgroundColor: "rgb(248, 215, 218)",
          borderColor: "rgb(220, 53, 69)",
          borderWidth: 1.5
        }
      ]
    };

    this.setState({ chartData: newChartData, loading: false }, this.initializeChart);
  }

  initializeChart() {
    const { chartData } = this.state;

    if (this.chartInstance) {
      this.chartInstance.destroy(); 
    }

    if (this.canvasRef.current) {
      const chartOptions = {
        legend: false,
        tooltips: {
          enabled: false,
          mode: "index",
          position: "nearest"
        },
        scales: {
          xAxes: [{ stacked: true, gridLines: false }],
          yAxes: [{
            stacked: true,
            ticks: {
              userCallback(label) {
                return label > 999 ? `${(label / 1000).toFixed(0)}k` : label;
              }
            }
          }]
        },
        ...this.props.chartOptions
      };

      this.chartInstance = new Chart(this.canvasRef.current, {
        type: "bar",
        data: chartData,
        options: chartOptions
      });

      if (this.legendRef.current) {
        this.legendRef.current.innerHTML = this.chartInstance.generateLegend();
      }

      this.chartInstance.render();
    }
  }

  render() {
    const { title } = this.props;
    const { loading, selectedYear } = this.state;

    return (
      <Card small className="h-100">
        <CardHeader className="border-bottom">
          <h6 className="m-0">{title}</h6>
          <div className="block-handle" />
        </CardHeader>

        <CardBody className="pt-0">
          <Row className="border-bottom py-2 bg-light">
            <Col sm="6" className="col d-flex mb-2 mb-sm-0">
              <select
                value={selectedYear}
                onChange={this.handleYearChange}
                className="form-control"
                style={{
                  width: "80px",
                  fontWeight: "bold",
                  textAlign: "center"
                }}>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
              </select>
            </Col>
          </Row>
          <div ref={this.legendRef} />
          {loading ? (
            <div className="spinner" /> 
          ) : (
            <canvas
              height="120"
              ref={this.canvasRef}
              style={{ maxWidth: "100% !important" }}
              className="sales-overview-sales-report"
            />
          )}
        </CardBody>
      </Card>
    );
  }
}

SalesReport.propTypes = {
  title: PropTypes.string,
  os: PropTypes.string.isRequired, 
  chartData: PropTypes.object,
  chartOptions: PropTypes.object
};

SalesReport.defaultProps = {
  title: "Sales Report",
  chartData: {
    labels: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ],
    datasets: [
      {
        label: "Safe Device",
        fill: "start",
        data: Array(12).fill(0),
        backgroundColor: "rgb(212, 237, 218)",
        borderColor: "rgb(40, 167, 69)",
        borderWidth: 1.5
      },
      {
        label: "Partially Safe Device",
        fill: "start",
        data: Array(12).fill(0),
        backgroundColor: "rgb(255, 243, 205)",
        borderColor: "rgb(255, 193, 7)",
        borderWidth: 1.5
      },
      {
        label: "Unsafe Device",
        fill: "start",
        data: Array(12).fill(0),
        backgroundColor: "rgb(248, 215, 218)",
        borderColor: "rgb(220, 53, 69)",
        borderWidth: 1.5
      }
    ]
  }
};

export default SalesReport;
